import React, { createContext, useContext, useState, useEffect } from "react";

export type SnapshotChartsContextType = {
  showNews: boolean | null;
  setShowNews: React.Dispatch<React.SetStateAction<boolean | null>>;
};

export const SnapshotChartsContext = createContext<
  SnapshotChartsContextType | undefined
>(undefined);

export const useSnapshotCharts = () => {
  const context = useContext(SnapshotChartsContext);

  if (!context)
    throw new Error(
      "useSnapshotCharts must be used within a SnapshotsChartsProvider"
    );

  return context;
};

const SHOW_NEWS_STORAGE_KEY = "show_news";
const SHOW_NEWS_DEFAULT = true;

function persistShowNews(nextShouldShowNews: boolean) {
  localStorage.setItem(SHOW_NEWS_STORAGE_KEY, `${nextShouldShowNews}`);
}

function shouldShowNews() {
  const persistedShowNews = localStorage.getItem(SHOW_NEWS_STORAGE_KEY);

  if (persistedShowNews === null) {
    return SHOW_NEWS_DEFAULT;
  }

  return persistedShowNews === "true";
}

interface SnapshotsChartsProviderProps {
  children: React.ReactNode;
}

export const SnapshotsChartsProvider: React.FC<
  SnapshotsChartsProviderProps
> = ({ children }) => {
  const [showNews, setShowNews] = useState<boolean | null>(null);

  useEffect(() => {
    setShowNews(shouldShowNews());
  }, []);

  useEffect(() => {
    if (showNews !== null) {
      persistShowNews(showNews);
    }
  }, [showNews]);

  return (
    <SnapshotChartsContext.Provider
      value={{
        showNews,
        setShowNews,
      }}
    >
      {children}
    </SnapshotChartsContext.Provider>
  );
};
